import { useContext, useEffect } from 'react';
import AppContext from '@/AppContext';
import apiClient from './apiClient';
import { useRefreshToken } from './hooks/useRefreshToken';
import { useSession } from 'next-auth/react';
import formatErrorMessage from '../utils/extractErrorMessages';
import statusCodes from '@/helpers/statusCodes';

const AxiosErrorHandler = ({ children }) => {
  const { data: session } = useSession();
  const context = useContext(AppContext);
  const refreshToken = useRefreshToken();

  useEffect(() => {
    const responseInterceptor = apiClient.interceptors.response.use(
      async (response) => {
        if (response.status !== 200)
          context.setNotification({
            type: 'success',
            title: statusCodes[response.status],
          });
        let { data } = response;
        return data;
      },

      async (error) => {
        const prevRequest = error?.config;
        if (error?.response?.status === 401 && !prevRequest?.sent) {
          prevRequest.sent = true;
          try {
            await refreshToken();
            if (session?.access) {
              prevRequest.headers['Authorization'] = `Bearer ${session.access}`;
              return apiClient(prevRequest);
            } else {
              // If refreshToken fails to update the session, reject with original error
              return Promise.reject(error);
            }
          } catch (refreshError) {
            // Handle refresh token failure
            return Promise.reject(error);
          }
        }

        // Show notification for error response
        if (error && error.response && error.response.data) {
          context.setNotification({
            type: 'warning',
            description: formatErrorMessage(error.response.data),
          });
        }
        
        return Promise.reject(error);
      }
    );

    return () => {
      apiClient.interceptors.response.eject(responseInterceptor);
    };
  }, [context, session, refreshToken]);
  return <>{children}</>;
};

export default AxiosErrorHandler;
